import React, { useEffect, useState } from "react";
import API_BASE_URL from "../../config";

function formatActivityDate(value) {
  if (!value) return "";

  try {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleString();
  } catch {
    return String(value);
  }
}

function getActivityTypeLabel(activityType, isSystemGenerated) {
  if (!isSystemGenerated) return "Note";

  const labels = {
    note: "Note",
    plan_changed: "Plan Changed",
    payment_received: "Payment Received",
    payment_failed: "Payment Failed",
    status_changed: "Status Changed",
    trial_started: "Trial Started",
    trial_ended: "Trial Ended",
    email_sent: "Email Activity",
    call_logged: "Call Logged",
    converted_to_subscriber: "Converted",
    subscriber_created: "Subscriber Created",
    account_activated: "Account Activated",
    billing_status_changed: "Billing Status",
    subscription_status_changed: "Subscription Status",
    subscriber_status_changed: "Subscriber Status",
    provisioning_status_changed: "Provisioning Status",
    billing_frequency_changed: "Billing Frequency",
    monthly_amount_changed: "Monthly Amount",
    scheduled_cancellation_changed: "Scheduled Cancellation",
    domain_approved: "Domain Approved",
    workspace_provisioned: "Workspace Ready",
    subscriber_activated: "Subscriber Activated",
    cancellation_scheduled: "Cancellation Scheduled",
    subscriber_reactivated: "Subscriber Reactivated",
    sales_lead_created: "Sales Lead Created",
    sales_lead_status_changed: "Lead Status",
    sales_lead_source_changed: "Lead Source",
    sales_lead_agency_changed: "Agency Name",
    owner_changed: "Owner Changed"
  };

  return labels[activityType] || "Activity";
}

function ActivityLogPanel({
  entityType,
  entityId,
  title = "Notes",
  description = "Capture context, follow-ups, and communication history."
}) {
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [error, setError] = useState("");

  const loadActivityLogs = async () => {
    try {
      if (!entityType || !entityId) return;

      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/activity-logs/${entityType}/${entityId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to load activity logs");
      }

      setActivityLogs(Array.isArray(data.activityLogs) ? data.activityLogs : []);
    } catch (err) {
      console.error("Load activity logs error:", err.message);
      setError(err.message || "Failed to load activity logs");
      setActivityLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActivityLogs();
  }, [entityType, entityId]);

  const handleAddNote = async (e) => {
    e.preventDefault();

    try {
      const cleanNote = String(noteBody || "").trim();

      if (!cleanNote) return;

      setSavingNote(true);
      setError("");

      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/activity-logs/${entityType}/${entityId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            title: "Note added",
            body: cleanNote
          })
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to add note");
      }

      setNoteBody("");

      if (data.activityLog) {
        setActivityLogs((prev) => [data.activityLog, ...prev]);
      } else {
        loadActivityLogs();
      }
    } catch (err) {
      console.error("Add activity note error:", err.message);
      alert(err.message || "Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div className="sales-section-card sales-section-lead">
      <div className="sales-section-head" style={{ marginBottom: "14px" }}>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>

      <form onSubmit={handleAddNote} style={{ marginBottom: "18px" }}>
        <div className="sales-field">
          <label>Add Note</label>
          <textarea
            className="sales-note-textarea"
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            placeholder="Add a concise note, follow-up detail, or internal comment..."
          />
        </div>

        <div style={{ marginTop: "12px" }}>
          <button type="submit" className="sales-primary-btn" disabled={savingNote}>
            {savingNote ? "Adding..." : "Add Note"}
          </button>
        </div>
      </form>

      {error && (
        <div className="profile-empty-card" style={{ color: "#b91c1c" }}>
          {error}
        </div>
      )}

      {loading && activityLogs.length === 0 ? (
        <div className="profile-empty-card">
          Loading notes...
        </div>
      ) : activityLogs.length > 0 ? (
        <div className="profile-note-list">
          {activityLogs.map((log) => {
            const isSystemGenerated =
              log.is_system_generated === true ||
              log.is_system_generated === 1 ||
              log.is_system_generated === "1";

            const label = getActivityTypeLabel(log.activity_type, isSystemGenerated);
            const body = log.body || log.note || "";

            return (
              <div key={log.id} className="profile-note-card">
                <div className="profile-note-card-header">
                  <div className="profile-note-type">
                    {label}
                  </div>

                  <div className="profile-note-date">
                    {formatActivityDate(log.created_at)}
                  </div>
                </div>

                <div className="profile-note-body">
                  {body}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="profile-empty-card">
          No notes available.
        </div>
      )}
    </div>
  );
}

export default ActivityLogPanel;